import React from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Filter,
  Loader2,
  RefreshCw,
  Save,
  Send,
  Search,
  Users,
  X,
} from 'lucide-react';
import { summativeExamService } from '../../../services/summativeExamService';
import { systemService } from '../../../services/systemService';
import { getActiveEnrollmentContext } from '../../../services/enrollmentService';
import { downloadSummativeMarksheetPdf } from '../utils/summativeMarksheetPdf';

const number = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const gradeFor = value => {
  const score = number(value);
  if (score >= 80) return { letter: 'A', rating: '5' };
  if (score >= 65) return { letter: 'B', rating: '4' };
  if (score >= 50) return { letter: 'C', rating: '3' };
  if (score >= 30) return { letter: 'D', rating: '2' };
  return { letter: 'F', rating: '1' };
};

const programKey = entry => String(entry?.program_id || entry?.program_code || entry?.program_name || 'programme');
const unitKey = entry => String(entry?.unit_id || entry?.unit_code || entry?.unit_name || 'unit');

const SummativeMarksheetModal = ({ onClose, institution, entry, rows }) => {
  const [downloading, setDownloading] = React.useState(false);
  const previewRows = rows.map(row => ({
    ...row,
    converted_marks: row.converted_marks ?? (row.raw_marks === '' || row.raw_marks == null ? null : Math.round(number(row.raw_marks) * 0.4)),
    grade_letter: row.grade_letter || (row.raw_marks === '' || row.raw_marks == null ? null : gradeFor(row.raw_marks).letter),
    rating: row.rating || (row.raw_marks === '' || row.raw_marks == null ? null : gradeFor(row.raw_marks).rating),
  }));

  const download = async () => {
    setDownloading(true);
    try {
      await downloadSummativeMarksheetPdf({
        institution,
        facultyName: entry?.faculty_name,
        departmentName: entry?.department_name,
        programName: entry?.program_name || entry?.program_code,
        unitName: entry?.unit_name,
        unitCode: entry?.unit_code,
        semesterName: entry?.semester_name,
        academicYear: entry?.academic_year_name,
        students: previewRows,
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-slate-950/55 p-3 sm:p-5">
      <div className="mx-auto my-3 w-full max-w-6xl overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-800 px-4 py-3 text-white">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-blue-300" />
            <div>
              <h2 className="text-sm font-semibold">Summative marksheet preview</h2>
              <p className="text-[11px] text-slate-300">{entry?.unit_code || 'Unit'} · {previewRows.length} paid bookings</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={download} disabled={downloading} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold hover:bg-blue-700 disabled:opacity-60">
              {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              {downloading ? 'Generating…' : 'Download PDF'}
            </button>
            <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-300 hover:bg-slate-700 hover:text-white" aria-label="Close marksheet preview"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="max-h-[calc(100vh-7rem)] overflow-y-auto bg-slate-100 p-3 sm:p-6">
          <div className="mx-auto w-full max-w-[297mm] border border-slate-200 bg-white p-6 text-black shadow-lg" style={{ fontFamily: '"Times New Roman", Times, serif' }}>
            <div className="mb-4 text-center">
              {institution?.logo_url ? <img src={institution.logo_url} alt="Institution crest" className="mx-auto mb-2 h-16 w-auto object-contain" /> : <div className="mx-auto mb-2 grid h-14 w-14 place-items-center rounded-full border-2 border-slate-800 text-[10px] font-bold">LOGO</div>}
              <h1 className="text-lg font-bold uppercase tracking-wide">{institution?.name || 'Institution'}</h1>
              {entry?.faculty_name && <h2 className="text-sm font-bold uppercase">{entry.faculty_name}</h2>}
              {entry?.department_name && <h3 className="text-xs font-semibold uppercase">{entry.department_name}</h3>}
              <h4 className="mt-1 text-sm font-bold uppercase underline">Summative Examination Marksheet</h4>
              <p className="text-xs font-bold uppercase underline">{entry?.semester_name || 'Semester'} Examinations</p>
              <p className="text-xs font-bold underline">{entry?.academic_year_name || ''}</p>
              <p className="text-xs font-bold uppercase">{entry?.program_name || entry?.program_code || ''}</p>
              <p className="text-xs font-bold uppercase">{entry?.unit_name ? `${entry.unit_name} - ` : ''}{entry?.unit_code || ''}</p>
            </div>
            <div className="border-t-2 border-black" />
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[920px] border-collapse border border-black text-xs">
                <thead><tr className="bg-slate-50 font-bold"><th className="border border-black px-2 py-1.5">Serial No#</th><th className="border border-black px-3 py-1.5 text-left">Name Of Student</th><th className="border border-black px-3 py-1.5 text-left">REG NO</th><th className="border border-black px-2 py-1.5">FA (/60)</th><th className="border border-black px-2 py-1.5">ATTENDANCE (%)</th><th className="border border-black px-2 py-1.5">SUMMATIVE (/100)</th><th className="border border-black px-2 py-1.5">CONVERTED (/40)</th><th className="border border-black px-2 py-1.5">GRADE</th><th className="border border-black px-2 py-1.5">RATING</th><th className="border border-black px-2 py-1.5">STATUS</th></tr></thead>
                <tbody>{previewRows.length ? previewRows.map((row, index) => <tr key={row.booking_id || index}><td className="border border-black px-2 py-1 text-center">{index + 1}</td><td className="border border-black px-3 py-1">{row.student_name || 'Unknown student'}</td><td className="border border-black px-3 py-1">{row.admission_number || '—'}</td><td className="border border-black px-2 py-1 text-center">{Math.round(number(row.formative_total_60))}</td><td className="border border-black px-2 py-1 text-center">{row.attendance_recorded ? `${number(row.attendance_percentage).toFixed(2)}%` : '--'}</td><td className="border border-black px-2 py-1 text-center">{row.raw_marks == null || row.raw_marks === '' ? '--' : number(row.raw_marks).toFixed(2)}</td><td className="border border-black px-2 py-1 text-center">{row.converted_marks == null ? '--' : Math.round(number(row.converted_marks))}</td><td className="border border-black px-2 py-1 text-center">{row.grade_letter || '--'}</td><td className="border border-black px-2 py-1 text-center">{row.rating || '--'}</td><td className="border border-black px-2 py-1 text-center">{!row.attendance_recorded ? 'Attendance pending' : number(row.attendance_percentage) < 70 ? 'Below 70%' : row.raw_marks == null || row.raw_marks === '' ? 'Mark pending' : 'Marked'}</td></tr>) : <tr><td colSpan="10" className="border border-black px-3 py-5 text-center italic">No paid summative bookings found.</td></tr>}</tbody>
              </table>
            </div>
            <div className="mt-4 text-xs font-medium"><span className="font-bold underline">KEY:</span><span className="ml-3">Summative mark is out of 100</span><span className="ml-4">Converted mark is out of 40</span><span className="ml-4">Rating: 1–5</span></div>
            <div className="mt-10 grid grid-cols-3 gap-8 border-t border-slate-300 pt-5 text-xs"><div><p className="font-bold uppercase">Trainer / Lecturer:</p><div className="my-5 border-b border-black" /><p>Name, Signature &amp; Date</p></div><div><p className="font-bold uppercase">Head of Department:</p><div className="my-5 border-b border-black" /><p>Name, Signature &amp; Date</p></div><div><p className="font-bold uppercase">External Verifier:</p><div className="my-5 border-b border-black" /><p>Name, Signature &amp; Date</p></div></div>
            <div className="mt-6 flex justify-between border-t border-slate-200 pt-1.5 text-[10px] text-slate-500"><span>Summative Examination Marksheet</span><span>Generated on {new Date().toLocaleDateString()}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummativeMarksEntry = () => {
  const [entries, setEntries] = React.useState([]);
  const [institution, setInstitution] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [notice, setNotice] = React.useState(null);
  const [activePeriod, setActivePeriod] = React.useState({ academicYearId: null, semesterId: null, academicYearName: '', semesterName: '' });
  const [search, setSearch] = React.useState('');
  const [programFilter, setProgramFilter] = React.useState('');
  const [levelFilter, setLevelFilter] = React.useState('');
  const [moduleFilter, setModuleFilter] = React.useState('');
  const [unitFilter, setUnitFilter] = React.useState('');
  const [marks, setMarks] = React.useState({});
  const [attendance, setAttendance] = React.useState({});
  const [remarks, setRemarks] = React.useState({});
  const [marksheetOpen, setMarksheetOpen] = React.useState(false);

  const load = React.useCallback(async (preservedValues = {}) => {
    setLoading(true);
    setNotice(null);
    try {
      const [activeContextResult, institutionDetails, assignmentPayload] = await Promise.all([
        getActiveEnrollmentContext(),
        systemService.getInstitutionDetails(),
        summativeExamService.getTrainerAssignedUnits(),
      ]);
      const activeContext = activeContextResult?.success ? activeContextResult.data : null;
      const academicYear = activeContext?.academicYear;
      const semester = activeContext?.semester;
      const period = {
        academicYearId: academicYear?.id || null,
        semesterId: semester?.id || null,
        academicYearName: academicYear?.name || '',
        semesterName: semester?.name || '',
      };
      setActivePeriod(period);
      if (!period.academicYearId || !period.semesterId) {
        setNotice({
          type: 'error',
          text: activeContextResult?.error || 'No active academic year and semester are configured for marks entry.',
        });
      }
      const roster = period.academicYearId && period.semesterId
        ? await summativeExamService.getTrainerMarksRoster({
          academicYearId: period.academicYearId,
          semesterId: period.semesterId,
        })
        : { entries: [] };
      const assignedUnits = Array.isArray(assignmentPayload?.units) ? assignmentPayload.units : [];
      const assignedKeys = new Set(assignedUnits.flatMap(unit => [unit.unit_id, unit.unit_code].filter(Boolean).map(String)));
      const rosterEntries = Array.isArray(roster?.entries) ? roster.entries : [];
      // Keep the client-side assignment gate even when the database returns
      // no assignments. An empty assignment catalogue must produce an empty
      // roster, never a fallback to every booked student in the database.
      const nextEntries = rosterEntries.filter(entry => [entry.unit_id, entry.unit_code].filter(Boolean).some(value => assignedKeys.has(String(value))));
      setEntries(nextEntries);
      setInstitution(institutionDetails);
      const preservedMarks = preservedValues.marks || {};
      const preservedAttendance = preservedValues.attendance || {};
      setMarks(Object.fromEntries(nextEntries.map(entry => {
        const savedValue = entry.raw_marks == null ? preservedMarks[entry.booking_id] : entry.raw_marks;
        return [entry.booking_id, savedValue == null ? '' : String(savedValue)];
      })));
      setAttendance(Object.fromEntries(nextEntries.map(entry => {
        // Some older roster responses omit the boolean flag even though an
        // attendance percentage has already been stored. Treat the value
        // itself as evidence that the field was recorded.
        const hasSavedAttendance = entry.attendance_recorded
          || entry.attendance_percentage != null
          || entry.attendance_recorded_at != null;
        const savedValue = hasSavedAttendance
          ? entry.attendance_percentage
          : preservedAttendance[entry.booking_id];
        return [entry.booking_id, savedValue == null ? '' : String(savedValue)];
      })));
      setRemarks(Object.fromEntries(nextEntries.map(entry => [entry.booking_id, entry.remarks || ''])));
    } catch (error) {
      setNotice({ type: 'error', text: error?.message || 'Unable to load summative marks.' });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const programs = React.useMemo(() => {
    const map = new Map();
    entries.forEach(entry => {
      const id = programKey(entry);
      if (!map.has(id)) map.set(id, { id, label: entry.program_name || entry.program_code || 'Programme' });
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [entries]);

  React.useEffect(() => {
    if (!programs.length) {
      setProgramFilter('');
      setLevelFilter('');
      setModuleFilter('');
      return;
    }
    if (!programs.some(program => program.id === programFilter)) setProgramFilter(programs[0].id);
  }, [programs, programFilter]);

  const programmeEntries = React.useMemo(
    () => entries.filter(entry => programFilter && programKey(entry) === programFilter),
    [entries, programFilter],
  );

  const levels = React.useMemo(() => [...new Set(programmeEntries
    .map(entry => entry.program_level)
    .filter(value => value !== null && value !== undefined && String(value) !== '')
    .map(String))].sort((a, b) => Number(a) - Number(b)), [programmeEntries]);

  React.useEffect(() => {
    if (!levels.length) {
      setLevelFilter('');
      return;
    }
    if (!levels.includes(levelFilter)) setLevelFilter(levels[0]);
  }, [levels, levelFilter]);

  const levelEntries = React.useMemo(
    () => programmeEntries.filter(entry => !levelFilter || String(entry.program_level || '') === levelFilter),
    [programmeEntries, levelFilter],
  );

  const modules = React.useMemo(() => [...new Set(levelEntries
    .map(entry => entry.module_number)
    .filter(value => value !== null && value !== undefined && String(value) !== '')
    .map(String))].sort((a, b) => Number(a) - Number(b)), [levelEntries]);

  React.useEffect(() => {
    if (!modules.length) {
      setModuleFilter('');
      return;
    }
    if (!modules.includes(moduleFilter)) setModuleFilter(modules[0]);
  }, [modules, moduleFilter]);

  const moduleEntries = React.useMemo(
    () => levelEntries.filter(entry => !moduleFilter || String(entry.module_number || '') === moduleFilter),
    [levelEntries, moduleFilter],
  );

  const units = React.useMemo(() => {
    const map = new Map();
    moduleEntries.forEach(entry => {
      const id = unitKey(entry);
      if (map.has(id)) return;
      map.set(id, {
      id,
      label: `${entry.unit_code || 'Unit'} · ${entry.unit_name || ''}`,
      entry,
      });
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [moduleEntries]);

  React.useEffect(() => {
    if (!units.length) {
      setUnitFilter('');
      return;
    }
    if (!units.some(unit => unit.id === unitFilter)) setUnitFilter(units[0].id);
  }, [units, unitFilter]);

  const filteredEntries = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    return entries.filter(entry => {
      if (!programFilter || programKey(entry) !== programFilter) return false;
      if (levelFilter && String(entry.program_level || '') !== levelFilter) return false;
      if (moduleFilter && String(entry.module_number || '') !== moduleFilter) return false;
      if (!unitFilter || unitKey(entry) !== unitFilter) return false;
      if (!term) return true;
      return [entry.student_name, entry.admission_number, entry.unit_code, entry.unit_name, entry.program_name]
        .some(value => String(value || '').toLowerCase().includes(term));
    });
  }, [entries, programFilter, levelFilter, moduleFilter, unitFilter, search]);

  const selectedUnitEntry = React.useMemo(() => {
    return units.find(unit => unit.id === unitFilter)?.entry || filteredEntries[0];
  }, [filteredEntries, unitFilter, units]);

  const updateMark = (bookingId, value) => {
    if (value === '') {
      setMarks(current => ({ ...current, [bookingId]: '' }));
      return;
    }
    const bounded = Math.min(100, Math.max(0, Number(value)));
    setMarks(current => ({ ...current, [bookingId]: Number.isFinite(bounded) ? String(bounded) : '' }));
  };

  const updateAttendance = (bookingId, value) => {
    if (value === '') {
      setAttendance(current => ({ ...current, [bookingId]: '' }));
      return;
    }
    const bounded = Math.min(100, Math.max(0, Number(value)));
    setAttendance(current => ({ ...current, [bookingId]: Number.isFinite(bounded) ? String(bounded) : '' }));
  };

  const saveMarks = async () => {
    // Save only the currently selected programme/unit. This keeps batches
    // isolated and prevents a filtered marksheet from writing another unit.
    const markRows = filteredEntries.filter(entry => marks[entry.booking_id] !== '' && marks[entry.booking_id] !== undefined && marks[entry.booking_id] !== null);
    const attendanceRows = filteredEntries.filter(entry => attendance[entry.booking_id] !== '' && attendance[entry.booking_id] !== undefined && attendance[entry.booking_id] !== null);
    if (!markRows.length && !attendanceRows.length) {
      setNotice({ type: 'error', text: 'Enter at least one attendance percentage or summative mark before saving.' });
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      for (const entry of markRows) {
        await summativeExamService.saveTrainerMark({
          bookingId: entry.booking_id,
          rawMarks: marks[entry.booking_id],
          remarks: remarks[entry.booking_id] || '',
        });
      }
      for (const entry of attendanceRows) {
        await summativeExamService.saveTrainerAttendance({
          bookingId: entry.booking_id,
          attendancePercentage: attendance[entry.booking_id],
        });
      }
      // Keep the values in the inputs even if the API read immediately after
      // the write is briefly stale. The next load will replace them with the
      // persisted values once they are visible through the roster RPC.
      await load({
        marks: Object.fromEntries(markRows.map(entry => [entry.booking_id, marks[entry.booking_id]])),
        attendance: Object.fromEntries(attendanceRows.map(entry => [entry.booking_id, attendance[entry.booking_id]])),
      });
      setNotice({ type: 'success', text: `${markRows.length} mark${markRows.length === 1 ? '' : 's'} and ${attendanceRows.length} attendance record${attendanceRows.length === 1 ? '' : 's'} saved successfully.` });
    } catch (error) {
      setNotice({ type: 'error', text: error?.message || 'Some summative marks could not be saved.' });
    } finally {
      setSaving(false);
    }
  };

  const openMarksheet = () => {
    if (!selectedUnitEntry) {
      setNotice({ type: 'error', text: 'Select a programme and unit before generating its marksheet.' });
      return;
    }
    const missingAttendance = filteredEntries.filter(entry => attendance[entry.booking_id] === '' || attendance[entry.booking_id] == null);
    if (missingAttendance.length) {
      setNotice({ type: 'error', text: `Enter attendance for all ${missingAttendance.length} paid candidate${missingAttendance.length === 1 ? '' : 's'} before downloading the marksheet.` });
      return;
    }
    setMarksheetOpen(true);
  };

  const submitForApproval = async () => {
    if (!selectedUnitEntry || !filteredEntries.length) return;
    const incompleteMarks = filteredEntries.some(entry => marks[entry.booking_id] === '' || marks[entry.booking_id] == null);
    const incompleteAttendance = filteredEntries.some(entry => attendance[entry.booking_id] === '' || attendance[entry.booking_id] == null);
    const incompleteCandidateCount = filteredEntries.filter(entry => marks[entry.booking_id] === '' || marks[entry.booking_id] == null).length;
    const incompleteAttendanceCount = filteredEntries.filter(entry => attendance[entry.booking_id] === '' || attendance[entry.booking_id] == null).length;
    setSubmitting(true);
    setNotice(null);
    try {
      await summativeExamService.submitResultBatch({
        academicYearId: selectedUnitEntry.academic_year_id,
        semesterId: selectedUnitEntry.semester_id,
        programId: selectedUnitEntry.program_id || selectedUnitEntry.program_code,
        unitId: selectedUnitEntry.unit_id || selectedUnitEntry.unit_code,
        programLevel: selectedUnitEntry.program_level,
        moduleNumber: selectedUnitEntry.module_number,
      });
      await load();
      setNotice({
        type: 'success',
        text: incompleteMarks
          ? `Marks submitted to the Head of Department. ${incompleteCandidateCount} candidate(s) without a summative mark are included as Absconded / incomplete examination.${incompleteAttendance ? ` Attendance is also missing for ${incompleteAttendanceCount} candidate(s).` : ''}`
          : incompleteAttendance
            ? `Marks submitted to the Head of Department. Attendance is missing for ${incompleteAttendanceCount} candidate(s), so they will be reviewed under the attendance outcome.`
          : 'Marks submitted to the Head of Department for approval.',
      });
    } catch (error) {
      setNotice({ type: 'error', text: error?.message || 'Unable to submit marks for approval.' });
    } finally {
      setSubmitting(false);
    }
  };

  const marksheetRows = filteredEntries.map(entry => ({
    ...entry,
    attendance_percentage: attendance[entry.booking_id] === '' || attendance[entry.booking_id] == null ? null : number(attendance[entry.booking_id]),
    attendance_recorded: attendance[entry.booking_id] !== '' && attendance[entry.booking_id] != null,
    raw_marks: marks[entry.booking_id] === '' || marks[entry.booking_id] == null ? null : number(marks[entry.booking_id]),
    converted_marks: marks[entry.booking_id] === '' || marks[entry.booking_id] == null ? null : Math.round(number(marks[entry.booking_id]) * 0.4),
    grade_letter: marks[entry.booking_id] === '' || marks[entry.booking_id] == null ? null : gradeFor(marks[entry.booking_id]).letter,
    rating: marks[entry.booking_id] === '' || marks[entry.booking_id] == null ? null : gradeFor(marks[entry.booking_id]).rating,
  }));

  return (
    <div className="space-y-3">
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">Trainer workspace</p>
            <div className="mt-0.5 flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight text-slate-950">Marks entry</h1>
              {selectedUnitEntry && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{filteredEntries.length} candidate{filteredEntries.length === 1 ? '' : 's'}</span>}
            </div>
            <p className="mt-0.5 text-xs text-slate-500">Only students registered for the active teaching period and enrolled in your assigned unit are listed.</p>
            <p className="mt-0.5 text-[11px] font-medium text-slate-500">Active period: {activePeriod.academicYearName || 'Not configured'} · {activePeriod.semesterName || 'Not configured'}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" onClick={openMarksheet} disabled={!filteredEntries.length || loading || filteredEntries.some(entry => attendance[entry.booking_id] === '' || attendance[entry.booking_id] == null)} title="Enter attendance for every paid candidate first" className="inline-flex items-center gap-1.5 rounded-md bg-blue-700 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"><Download className="h-3.5 w-3.5" />Generate marksheet</button>
            <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh</button>
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
           <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-700"><Filter className="h-4 w-4 text-primary" />Choose the teaching period, programme and unit</div>
           <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-[minmax(0,1.15fr)_minmax(120px,0.55fr)_minmax(120px,0.55fr)_minmax(0,1.25fr)_minmax(220px,0.9fr)]">
             <label className="min-w-0"><span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Programme</span><select value={programFilter} onChange={event => { setProgramFilter(event.target.value); setLevelFilter(''); setModuleFilter(''); setUnitFilter(''); }} disabled={!programs.length} className="h-10 w-full min-w-0 appearance-none rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"><option value="">Select programme</option>{programs.map(program => <option key={program.id} value={program.id}>{program.label}</option>)}</select></label>
             <label className="min-w-0"><span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Level</span><select value={levelFilter} onChange={event => { setLevelFilter(event.target.value); setModuleFilter(''); setUnitFilter(''); }} disabled={!levels.length} className="h-10 w-full min-w-0 appearance-none rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"><option value="">All levels</option>{levels.map(level => <option key={level} value={level}>Level {level}</option>)}</select></label>
             <label className="min-w-0"><span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Module</span><select value={moduleFilter} onChange={event => { setModuleFilter(event.target.value); setUnitFilter(''); }} disabled={!modules.length} className="h-10 w-full min-w-0 appearance-none rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"><option value="">All modules</option>{modules.map(module => <option key={module} value={module}>Module {module}</option>)}</select></label>
             <label className="min-w-0"><span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Unit</span><select value={unitFilter} onChange={event => setUnitFilter(event.target.value)} disabled={!units.length} className="h-10 w-full min-w-0 appearance-none rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"><option value="">Select unit</option>{units.map(unit => <option key={unit.id} value={unit.id}>{unit.label}</option>)}</select></label>
             <label className="relative min-w-0"><span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Find student</span><Search className="pointer-events-none absolute left-3 top-[2.15rem] h-4 w-4 -translate-y-1/2 text-slate-400" /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Name or admission number" className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" /></label>
          </div>
        </div>
        {selectedUnitEntry && <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-blue-100 bg-blue-50/70 px-3 py-2"><div className="min-w-0"><p className="text-[9px] font-semibold uppercase tracking-wide text-blue-700">Current selection</p><p className="mt-0.5 truncate text-xs font-semibold text-slate-900"><span className="font-mono text-blue-800">{selectedUnitEntry.unit_code || 'Unit'}</span><span className="mx-1.5 text-slate-400">·</span>{selectedUnitEntry.unit_name || ''}</p><p className="mt-0.5 truncate text-[11px] text-slate-600">{selectedUnitEntry.program_name || selectedUnitEntry.program_code || 'Programme'} · L{selectedUnitEntry.program_level || '—'} M{selectedUnitEntry.module_number || '—'}</p></div><span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-blue-700">{filteredEntries.length} paid</span></div>}
      </section>

      {notice && <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${notice.type === 'error' ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>{notice.type === 'error' ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> : <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />}<span>{notice.text}</span></div>}

      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2.5"><div><h2 className="text-sm font-bold text-slate-950">{selectedUnitEntry ? `${selectedUnitEntry.unit_code || 'Unit'} · paid candidates` : 'Select a programme and unit'}</h2><p className="text-[11px] text-slate-500">{filteredEntries.length} booking{filteredEntries.length === 1 ? '' : 's'} · attendance is out of 100%; marks are out of 100 and converted to 40.</p></div><div className="flex flex-wrap items-center gap-2"><button type="button" onClick={saveMarks} disabled={saving || submitting || loading || !filteredEntries.length} className="inline-flex items-center gap-1.5 rounded-md bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}{saving ? 'Saving…' : 'Save marks and attendance'}</button><button type="button" onClick={submitForApproval} disabled={saving || submitting || loading || !filteredEntries.length} title="Candidates without marks or attendance are included as absconded or incomplete examination records" className="inline-flex items-center gap-1.5 rounded-md bg-violet-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-50">{submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}{submitting ? 'Submitting…' : 'Submit to HoD'}</button></div></div>
       {loading ? <div className="grid min-h-48 place-items-center text-sm text-slate-500"><span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" />Loading paid bookings…</span></div> : !programFilter || !unitFilter ? <div className="grid min-h-48 place-items-center px-5 text-center"><Filter className="h-9 w-9 text-slate-300" /><p className="mt-2 text-sm font-semibold text-slate-700">Select a programme and unit</p><p className="mt-1 text-xs text-slate-500">Marks and attendance are entered one programme and one assigned unit at a time.</p></div> : filteredEntries.length === 0 ? <div className="grid min-h-48 place-items-center px-5 text-center"><Users className="h-9 w-9 text-slate-300" /><p className="mt-2 text-sm font-semibold text-slate-700">No paid candidates for this unit</p><p className="mt-1 text-xs text-slate-500">Students appear here after their summative examination booking fee is paid and posted.</p></div> : <div className="overflow-x-auto"><table className="min-w-[1080px] w-full text-left text-xs"><thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">SN</th><th className="px-4 py-3">Student No.</th><th className="px-4 py-3">Student name</th><th className="px-4 py-3 text-right">FA (/60)</th><th className="px-4 py-3">Attendance (%)</th><th className="px-4 py-3">Summative (/100)</th><th className="px-4 py-3 text-right">Converted (/40)</th><th className="px-4 py-3">Grade / rating</th><th className="px-4 py-3">Status</th></tr></thead><tbody className="divide-y divide-slate-100">{filteredEntries.map((entry, index) => { const mark = marks[entry.booking_id]; const attendanceValue = attendance[entry.booking_id]; const attendanceRecorded = attendanceValue !== '' && attendanceValue != null; const grade = mark === '' || mark == null ? null : gradeFor(mark); return <tr key={entry.booking_id} className="align-middle hover:bg-slate-50/60"><td className="px-4 py-3 text-slate-500">{index + 1}</td><td className="px-4 py-3 font-mono text-[11px] text-slate-700">{entry.admission_number || '—'}</td><td className="px-4 py-3 font-medium text-slate-900">{entry.student_name || 'Unknown student'}<p className="mt-0.5 text-[10px] text-slate-500">L{entry.program_level || '—'} M{entry.module_number || '—'}</p></td><td className="px-4 py-3 text-right font-semibold text-slate-800">{Math.round(number(entry.formative_total_60))}</td><td className="px-4 py-3"><input aria-label={`Attendance percentage for ${entry.student_name || entry.admission_number || 'student'}`} type="number" min="0" max="100" step="0.01" value={attendanceValue ?? ''} onChange={event => updateAttendance(entry.booking_id, event.target.value)} placeholder="e.g. 82" className="h-8 w-28 rounded-md border border-slate-200 px-2 text-xs focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" /></td><td className="px-4 py-3"><input aria-label={`Summative mark for ${entry.student_name || entry.admission_number || 'student'}`} type="number" min="0" max="100" step="0.01" value={mark ?? ''} onChange={event => updateMark(entry.booking_id, event.target.value)} className="h-8 w-28 rounded-md border border-slate-200 px-2 text-xs focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" /></td><td className="px-4 py-3 text-right font-semibold text-slate-800">{mark === '' || mark == null ? '—' : Math.round(number(mark) * 0.4)}</td><td className="px-4 py-3">{grade ? <span className="font-semibold text-slate-800">{grade.letter} <span className="text-slate-400">({grade.rating})</span></span> : <span className="text-slate-400">—</span>}</td><td className="px-4 py-3">{!attendanceRecorded ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-[10px] font-semibold text-amber-700"><AlertCircle className="h-3 w-3" />Attendance pending</span> : number(attendanceValue) < 70 ? <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-1 text-[10px] font-semibold text-rose-700"><AlertCircle className="h-3 w-3" />Below 70%</span> : mark === '' || mark == null ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-[10px] font-semibold text-amber-700"><AlertCircle className="h-3 w-3" />Mark pending</span> : <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" />Ready</span>}</td></tr>; })}</tbody></table></div>}
      </section>

      {marksheetOpen && selectedUnitEntry && <SummativeMarksheetModal onClose={() => setMarksheetOpen(false)} institution={institution} entry={selectedUnitEntry} rows={marksheetRows} />}
    </div>
  );
};

export default SummativeMarksEntry;
